import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import {
  VStack,
  SimpleGrid,
  FormControl,
  FormLabel,
  Switch,
  Checkbox as CheckboxChakra,
  HStack,
  Button,
  Box,
  ModalFooter,
  useToast,
  Flex,
} from '@chakra-ui/react';
import {
  FieldError,
  FieldValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import context from '../context';
import Datepicker from '../../Form/Datepicker';
import Input from '../../Form/Input';
import {
  CREATE_PAGE_GROUP,
  REMOVE_PAGE_GROUP,
  UPDATE_PAGE_GROUP,
} from '../../../graphQL/mutations';
import { GET_PAGE_DATA } from '../../../graphQL/queries';
import Textarea from '../../Form/Textarea';
import sanitizeString from '../../../utils/sanitizeString';
import Select from '../../Form/Select';
import Checkbox from '../../Form/Checkbox';
import sizeTypes from '../../../utils/sizeType';
import carouselBannerTypes from '../../../utils/carouselBannerTypes';

interface Props {
  onCreated?: (data: any) => void;
}
interface FormData {
  name: string;
  description?: string;
  height?: string;
  width?: string;
  countdown: boolean;
  schedule: boolean;
  schedule_start?: Date;
  schedule_end?: Date;
  padding: number;
  is_full_width_banner: boolean;
  round_shape?: boolean;
  size: {
    label: string | string;
    value: string | string;
  };
  type: {
    label: string | string;
    value: string | string;
  };
}

const formData = yup.object().shape({
  name: yup.string().nullable(),
  description: yup.string().nullable(),
  height: yup.string().nullable(),
  width: yup.string().nullable(),
  schedule: yup.boolean(),
  round_shape: yup.boolean(),
  countdown: yup.boolean(),
  is_full_width_banner: yup.boolean(),
  size: yup.mixed().when('is_full_width_banner', {
    is: true,
    then: yup.mixed().optional(),
    otherwise: yup.object().shape({
      value: yup.string().required('El tamaño es obligatorio'),
      label: yup.string().required('El tamaño es obligatorio'),
    }),
  }),
  type: yup.mixed().when('is_full_width_banner', {
    is: true,
    then: yup.mixed().optional(),
    otherwise: yup.object().shape({
      value: yup.string().required('El tipo es obligatorio'),
      label: yup.string().required('El tipo es obligatorio'),
    }),
  }),
  schedule_start: yup
    .mixed()
    .when('schedule', {
      is: true,
      then: yup.date().required('La fecha de inicio es obligatoria'),
    })
    .optional(),
  schedule_end: yup
    .mixed()
    .when(['schedule', 'schedule_start'], (schedule, schedule_start) => {
      if (!schedule) {
        return yup.date();
      }
      if (schedule_start) {
        return yup
          .date()
          .min(
            schedule_start,
            'La fecha final debe ser posterior a la fecha de inicio',
          )
          .required('La fecha final es obligatoria');
      }
      return yup.date().required('La fecha final es obligatoria');
    }),
});

const Form: React.FC<Props> = ({ onCreated }) => {
  const toast = useToast();

  const { pageGroup, newPageGroupDTO, onPageGroupCreated, onClose, display } =
    React.useContext(context);
  const { formState, handleSubmit, register, control, setValue } = useForm({
    resolver: yupResolver(formData),
  });

  const [createPageGroup, { loading: createLoading, error: createError }] =
    useMutation(CREATE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [updatePageGroup, { loading: updateLoading, error: updateError }] =
    useMutation(UPDATE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [removePageGroup, { loading: deleteLoading, error: deleteError }] =
    useMutation(REMOVE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [schedule, setSchedule] = useState(!!pageGroup?.schedule_end);
  const [fullWidth, setFullWidth] = useState(false);

  const { errors } = formState;
  const { t } = useTranslation();
  useEffect(() => {
    if (createError || updateError || deleteError) {
      toast({
        title: 'Opss!!',
        description:
          createError?.message ||
          updateError?.message ||
          deleteError?.message ||
          t('generalError'),
        status: 'error',
        position: 'top-right',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createError, updateError, deleteError]);

  useEffect(() => {
    if (!pageGroup) return;
    setValue('padding', pageGroup.padding);
    if (pageGroup?.schedule_end) {
      setValue('schedule', true);
      setValue('schedule_start', new Date(pageGroup.schedule_start));
      setValue('schedule_end', new Date(pageGroup.schedule_end));
      setValue('countdown', pageGroup.countdown);
    }
    setValue('description', pageGroup?.description);
    setValue('height', pageGroup?.height);
    setValue('width', pageGroup?.width);
    setValue('name', pageGroup.name);
    if (pageGroup?.is_full_width_banner) {
      setValue('is_full_width_banner', true);
      setFullWidth(true);
    }

    if (pageGroup?.round_shape) {
      setValue('round_shape', true);
    }

    setValue(
      'type',
      carouselBannerTypes.find(type => type?.value === pageGroup?.type),
    );
    setValue(
      'size',
      sizeTypes.find(size => size?.value === pageGroup?.size),
    );
  }, [pageGroup, setValue]);

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      const sanitizedObj = sanitizeString(data);

      const { schedule: scheduleData, ...rest } = sanitizedObj;

      if (pageGroup) {
        await updatePageGroup({
          variables: {
            updatePageGroupInput: {
              ...rest,
              schedule_end: scheduleData ? rest.schedule_end : null,
              padding: Number(data?.padding),
              countdown: scheduleData ? rest.countdown : false,
              id: pageGroup?.id,
              size: data.size?.value,
              is_full_width_banner: !!data.is_full_width_banner,
              round_shape: !!data.round_shape,
              type: data.type?.value,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
            },
          },
        });
        toast({
          title: t('success'),
          description: t('groupEditedSuccess'),
          status: 'success',
          position: 'top-right',
        });
      } else {
        const apiData = await createPageGroup({
          variables: {
            createPageGroupInput: {
              ...rest,
              ...newPageGroupDTO,
              element_display_id: display?.id,
              padding: Number(data?.padding),
              size: data.size?.value,
              is_full_width_banner: !!data.is_full_width_banner,
              round_shape: !!data.round_shape,
              type: data.type?.value,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
            },
          },
        });
        if (onCreated) onCreated(apiData);
        if (onPageGroupCreated) onPageGroupCreated(apiData);
        toast({
          title: t('success'),
          description: t('groupCreatedSuccess'),
          status: 'success',
          position: 'top-right',
        });
      }
    } catch {
      // nada
    }
  };

  const handleDelete = async () => {
    try {
      const confirmed = window.confirm(t('confirmDeleteGroup'));
      if (confirmed) {
        await removePageGroup({
          variables: {
            id: pageGroup?.id,
          },
        });
        toast({
          title: t('success'),
          description: t('groupDeletedSuccess'),
          status: 'success',
          position: 'top-right',
        });
        if (onClose) onClose();
      }
    } catch {
      // error
    }
  };

  return (
    <Box>
      <VStack
        as="form"
        spacing="8"
        minHeight="fit-content"
        onSubmit={handleSubmit(handleForm as SubmitHandler<FieldValues>)}
      >
        <SimpleGrid
          minChildWidth="200px"
          spacing="4"
          w="100%"
          minHeight="fit-content"
        >
          <Input
            label={t('title')}
            error={errors?.name as FieldError | undefined}
            {...register('name')}
          />
          <Input
            label={t('widthPx')}
            placeholder="Ex: 120px"
            type="number"
            isDisabled={fullWidth}
            error={errors?.width as FieldError | undefined}
            {...register('width')}
          />
          <Input
            label={t('heightPx')}
            placeholder="Ex: 150px"
            type="number"
            error={errors?.height as FieldError | undefined}
            {...register('height')}
          />
          <Input
            label={`${t('padding')} (px)`}
            type="number"
            isRequired
            error={errors?.padding as FieldError | undefined}
            {...register('padding')}
          />
        </SimpleGrid>
        <Textarea
          label={t('subTitle')}
          error={errors?.description as FieldError | undefined}
          {...register('description')}
        />
        <Select
          name="size"
          control={control}
          label={t('size')}
          width="150px"
          error={errors.size as FieldError | undefined}
          isRequired={!fullWidth}
          isDisabled={fullWidth}
          options={sizeTypes}
          onChange={() => {
            setValue('size', '');
          }}
        />
        <Select
          name="type"
          width="150px"
          isRequired={!fullWidth}
          isDisabled={fullWidth}
          control={control}
          label={t('type')}
          error={errors.type as FieldError | undefined}
          options={carouselBannerTypes}
          onChange={() => {
            setValue('type', '');
          }}
        />
        <Flex width="100%" justifyContent="left">
          <Checkbox
            control={control}
            label={t('roundShape')}
            {...register('round_shape')}
          />
        </Flex>
        <Flex width="100%" justifyContent="left">
          <CheckboxChakra
            {...register('is_full_width_banner')}
            isChecked={fullWidth}
            onChange={e => {
              setFullWidth(e?.target?.checked);
              if (e.target.checked) {
                setValue('size', '');
                setValue('type', '');
                setValue('width', '');
              }
            }}
          >
            {t('fullWidth')}
          </CheckboxChakra>
        </Flex>
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="schedule" mb="0">
            {t('schedule')}
          </FormLabel>
          <Switch
            id="schedule"
            size="lg"
            colorScheme="brand"
            isChecked={schedule}
            {...register('schedule')}
            onChange={() => setSchedule(state => !state)}
          />
        </FormControl>
        {schedule && (
          <>
            <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
              <Datepicker
                isRequired
                control={control}
                {...register('schedule_start')}
                label={t('scheduleStart')}
                showTimeSelect
                error={errors?.schedule_start as FieldError | undefined}
              />
              <Datepicker
                isRequired
                control={control}
                {...register('schedule_end')}
                label={t('scheduleEnd')}
                showTimeSelect
                error={errors?.schedule_end as FieldError | undefined}
              />
            </SimpleGrid>
            <CheckboxChakra
              alignSelf="flex-start"
              colorScheme="brand"
              {...register('countdown')}
            >
              {t('countdown')}
            </CheckboxChakra>
          </>
        )}
      </VStack>

      <ModalFooter>
        <HStack spacing={4}>
          {!!pageGroup && (
            <Button
              onClick={handleDelete}
              colorScheme="red"
              isLoading={deleteLoading}
            >
              {t('remove')}
            </Button>
          )}
          <Button onClick={onClose}>{t('close')}</Button>
          <Button
            colorScheme="brand"
            isLoading={createLoading || updateLoading}
            onClick={handleSubmit(handleForm as SubmitHandler<FieldValues>)}
          >
            {t('save')}
          </Button>
        </HStack>
      </ModalFooter>
    </Box>
  );
};

export default Form;
